import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DataTable } from '../components/DataTable'
import { cn } from '../../../lib/utils'

const useAuditLog = () =>
  useQuery({
    queryKey: ['admin', 'audit-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select(`
          id, action, details, created_at,
          admin:profiles!admin_audit_log_admin_user_id_fkey(email),
          target:profiles!admin_audit_log_target_user_id_fkey(email),
          household:households(name)
        `)
        .order('created_at', { ascending: false })
        .limit(500)
      if (error) throw error
      return data ?? []
    },
  })

const ACTION_COLORS = {
  suspend_account:   'rose',
  unsuspend_account: 'emerald',
  delete_account:    'rose',
  grant_comp:        'violet',
  revoke_comp:       'amber',
  issue_refund:      'amber',
  reset_password:    'sky',
}

const ActionBadge = ({ action }) => {
  const color = ACTION_COLORS[action] ?? 'slate'
  const cls = {
    rose:    'bg-rose-100 text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    violet:  'bg-violet-100 text-violet-700',
    amber:   'bg-amber-100 text-amber-700',
    sky:     'bg-sky-100 text-sky-700',
    slate:   'bg-background text-text-muted border border-border',
  }[color]

  return (
    <span className={cn('text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full', cls)}>
      {action}
    </span>
  )
}

export const AuditLogPage = () => {
  const { data: logs = [], isLoading } = useAuditLog()
  const [expandedId, setExpandedId]  = useState(null)
  const [actionFilter, setAction]    = useState('')
  const [dateFrom, setDateFrom]      = useState('')
  const [dateTo, setDateTo]          = useState('')

  const actionTypes = [...new Set(logs.map((l) => l.action))].sort()

  const filtered = logs.filter((l) => {
    if (actionFilter && l.action !== actionFilter) return false
    if (dateFrom && new Date(l.created_at) < new Date(dateFrom)) return false
    if (dateTo   && new Date(l.created_at) > new Date(dateTo + 'T23:59:59')) return false
    return true
  })

  const columns = [
    {
      key: 'created_at', label: 'Timestamp', sortable: true,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
      className: 'whitespace-nowrap',
    },
    {
      key: 'admin', label: 'Admin',
      render: (_, row) => row.admin?.email ?? row.admin_user_id ?? '—',
    },
    {
      key: 'action', label: 'Action',
      render: (v) => <ActionBadge action={v} />,
    },
    {
      key: 'target', label: 'Target user',
      render: (_, row) => row.target?.email ?? '—',
    },
    {
      key: 'household', label: 'Target household',
      render: (_, row) => row.household?.name ?? '—',
    },
    {
      key: 'details', label: 'Details',
      render: (v, row) => (
        <button
          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
          className="text-xs text-primary hover:underline"
        >
          {expandedId === row.id ? 'Hide' : 'View'}
        </button>
      ),
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Audit Log</h1>
        <p className="text-sm text-text-muted mt-1">{filtered.length.toLocaleString()} entries</p>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        emptyMessage="No audit log entries."
        pageSize={50}
        actions={
          <>
            <select
              value={actionFilter}
              onChange={(e) => setAction(e.target.value)}
              className="rounded-base border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All actions</option>
              {actionTypes.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-base border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <span className="text-text-muted text-sm">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="rounded-base border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </>
        }
      />

      {expandedId && (() => {
        const log = logs.find((l) => l.id === expandedId)
        return log ? (
          <div className="mt-3 rounded-base border border-border bg-background p-4">
            <p className="text-xs font-semibold text-text-muted mb-2">Details for {log.action}</p>
            <pre className="text-xs text-text-secondary overflow-x-auto">
              {JSON.stringify(log.details, null, 2)}
            </pre>
          </div>
        ) : null
      })()}
    </div>
  )
}
